// ==================== DEMO DATA - SINGLE SOURCE OF TRUTH ====================
// Consolidates all demo contracts, deals, and documents used across demo pages
// For detailed contract analysis data, see demoContractData.ts
// For organization data, see demoOrganizationData.ts

import { ContractMetadata, ContractStatus } from "@equaliq/api-model";
import { DEMO_CONTRACT_IDS, DEMO_USER_IDS } from "./demoContractData";

// ==================== DEMO CONTRACTS (RAW DATA) ====================
// Uses ContractMetadata from API model + sharedWith for UI compatibility
// Note: These don't include React elements (icons) - those are added by components
export type DemoContract = ContractMetadata & {
  sharedWith: string[]; // For UI compatibility (API uses sharedUsers)
};

export const demoContractsData: DemoContract[] = [
  {
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    name: "Artist Recording Deal",
    type: "recording",
    status: ContractStatus.complete,
    uploadedOn: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    sharedWith: [DEMO_USER_IDS.ADAM_SILVER],
    isOwner: true,
  },
  {
    contractId: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
    name: "Producer Agreement",
    type: "producer",
    status: ContractStatus.complete,
    uploadedOn: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    sharedWith: [DEMO_USER_IDS.ROGER_GOODELL],
    isOwner: true,
  },
  {
    contractId: DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
    name: "Brand Partnership / NIL Deal",
    type: "services",
    status: ContractStatus.complete,
    uploadedOn: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    sharedWith: [DEMO_USER_IDS.ADAM_SILVER, DEMO_USER_IDS.ROGER_GOODELL],
    isOwner: true,
  },
  {
    contractId: DEMO_CONTRACT_IDS.SAFE_AGREEMENT,
    name: "SAFE Agreement",
    type: "services",
    status: ContractStatus.complete,
    uploadedOn: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    sharedWith: [DEMO_USER_IDS.ADAM_SILVER],
    isOwner: true,
  },
];

// ==================== DEMO DEALS ====================
export interface DemoDeal {
  id: string;
  name: string;
  type: string;
  status: "In Progress" | "Pending" | "Draft" | "Completed";
  value: string;
  date: string;
  updatedAt: string;
  documentCount: number;
  coreDocument: string;
  coreContractId?: string;
}

export const demoDeals: DemoDeal[] = [
  { id: "DEMO_DEAL_RECORDING", name: "Artist Recording Agreement", type: "Record Label", status: "In Progress", value: "$150,000", date: "Dec 15, 2024", updatedAt: "Jan 7, 2025", documentCount: 4, coreDocument: "Master Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-1", name: "Sony Music Partnership", type: "Record Label", status: "In Progress", value: "$250,000", date: "Dec 1, 2024", updatedAt: "Jan 6, 2025", documentCount: 6, coreDocument: "Recording Partnership Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-2", name: "Netflix Sync License", type: "Sync License", status: "Pending", value: "$45,000", date: "Nov 28, 2024", updatedAt: "Jan 5, 2025", documentCount: 2, coreDocument: "Sync License Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-3", name: "Touring Agreement 2025", type: "Touring", status: "Draft", value: "$180,000", date: "Nov 15, 2024", updatedAt: "Jan 3, 2025", documentCount: 1, coreDocument: "Touring Services Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-4", name: "Spotify Playlist Placement", type: "Distribution", status: "Completed", value: "$12,000", date: "Nov 10, 2024", updatedAt: "Dec 20, 2024", documentCount: 3, coreDocument: "Distribution Placement Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-5", name: "Nike Brand Ambassador", type: "Endorsement", status: "In Progress", value: "$500,000", date: "Nov 5, 2024", updatedAt: "Jan 8, 2025", documentCount: 8, coreDocument: "Brand Endorsement Contract", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-6", name: "Publishing Admin Agreement", type: "Publishing", status: "Draft", value: "$75,000", date: "Oct 28, 2024", updatedAt: "Dec 15, 2024", documentCount: 2, coreDocument: "Publishing Administration Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-7", name: "Feature Collaboration - Drake", type: "Collaboration", status: "Pending", value: "$150,000", date: "Oct 20, 2024", updatedAt: "Jan 2, 2025", documentCount: 3, coreDocument: "Featured Artist Collaboration Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-8", name: "Merchandise License - Hot Topic", type: "Merchandising", status: "Completed", value: "$35,000", date: "Oct 15, 2024", updatedAt: "Nov 30, 2024", documentCount: 5, coreDocument: "Merchandise License Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-9", name: "Film Score Commission", type: "Sync License", status: "In Progress", value: "$200,000", date: "Oct 10, 2024", updatedAt: "Jan 4, 2025", documentCount: 4, coreDocument: "Film Score Commission Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
  { id: "deal-10", name: "Management Agreement", type: "Management", status: "Completed", value: "$85,000", date: "Oct 1, 2024", updatedAt: "Oct 15, 2024", documentCount: 2, coreDocument: "Artist Management Agreement", coreContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL },
];

// ==================== DEMO DOCUMENTS (for Deal pages) ====================
export interface DemoDocument {
  id: string;
  name: string;
  type: string;
  status: "signed" | "pending_review" | "draft";
  linkedAnalysis: boolean;
  uploadedAt: string;
  updatedAt: string;
  size: string;
  uploadedBy: string;
}

export const demoDocuments: DemoDocument[] = [
  { id: "doc-1", name: "Master Agreement", type: "Contract", status: "signed", linkedAnalysis: true, uploadedAt: "Dec 1, 2024", updatedAt: "Jan 5, 2025", size: "2.4 MB", uploadedBy: "You" },
  { id: "doc-2", name: "Amendment #1", type: "Amendment", status: "pending_review", linkedAnalysis: true, uploadedAt: "Dec 5, 2024", updatedAt: "Jan 7, 2025", size: "856 KB", uploadedBy: "Legal Team" },
  { id: "doc-3", name: "Side Letter - Royalties", type: "Side Letter", status: "draft", linkedAnalysis: false, uploadedAt: "Dec 8, 2024", updatedAt: "Jan 8, 2025", size: "124 KB", uploadedBy: "You" },
  { id: "doc-4", name: "Payment Schedule", type: "Schedule", status: "signed", linkedAnalysis: false, uploadedAt: "Dec 1, 2024", updatedAt: "Dec 15, 2024", size: "98 KB", uploadedBy: "Finance" },
  { id: "doc-5", name: "Territory Rights Exhibit", type: "Exhibit", status: "signed", linkedAnalysis: true, uploadedAt: "Dec 1, 2024", updatedAt: "Dec 10, 2024", size: "1.1 MB", uploadedBy: "You" },
  { id: "doc-6", name: "NDA - Confidentiality", type: "NDA", status: "signed", linkedAnalysis: false, uploadedAt: "Nov 15, 2024", updatedAt: "Nov 20, 2024", size: "245 KB", uploadedBy: "Legal Team" },
];

// ==================== DEMO ACTION ITEMS ====================
export interface DemoActionItem {
  id: string;
  title: string;
  priority: "high" | "medium" | "low";
  dueDate: string;
  assignee: string;
  completed: boolean;
  type: "review" | "signature" | "document";
}

export const demoActionItems: DemoActionItem[] = [
  { id: "action-1", title: "Review Amendment #1 changes", priority: "high", dueDate: "Dec 15, 2024", assignee: "You", completed: false, type: "review" },
  { id: "action-2", title: "Sign Side Letter - Royalties", priority: "medium", dueDate: "Dec 20, 2024", assignee: "You", completed: false, type: "signature" },
  { id: "action-3", title: "Upload proof of insurance", priority: "low", dueDate: "Dec 30, 2024", assignee: "You", completed: false, type: "document" },
  { id: "action-4", title: "Confirm territory rights", priority: "medium", dueDate: "Dec 18, 2024", assignee: "Legal Team", completed: true, type: "review" },
  { id: "action-5", title: "Initial contract review", priority: "high", dueDate: "Nov 30, 2024", assignee: "You", completed: true, type: "review" },
];

// ==================== DEAL STAGES ====================
export interface DealStage {
  id: string;
  label: string;
  completed: boolean;
  current?: boolean;
}

export const demoDealStages: DealStage[] = [
  { id: "drafting", label: "Drafting", completed: true },
  { id: "signing", label: "Signing", completed: false, current: true },
  { id: "delivery", label: "Delivery", completed: false },
];

// ==================== DEAL PARTICIPANTS ====================
// Re-export types and labels from constants for backward compatibility
export type { ParticipantRole, ParticipantPermission } from "@/constants/participant.constants";
export { participantRoleLabels, participantPermissionLabels } from "@/constants/participant.constants";

import type { ParticipantRole, ParticipantPermission } from "@/constants/participant.constants";

export interface DemoParticipant {
  id: string;
  name: string;
  email: string;
  role: ParticipantRole;
  permission: ParticipantPermission;
  avatar?: string;
  userId?: string; // Links to DEMO_USER_IDS if available
  invitedAt?: string;
  status: "active" | "pending" | "declined";
}

// Initial demo participants for deals
export const demoParticipants: DemoParticipant[] = [
  {
    id: "participant-1",
    name: "Demo User",
    email: "demo@example.com",
    role: "owner",
    permission: "full_access",
    userId: DEMO_USER_IDS.DEMO_USER_1,
    status: "active",
  },
  {
    id: "participant-2",
    name: "Adam Silver",
    email: "adam.silver@example.com",
    role: "legal_counsel",
    permission: "edit",
    userId: DEMO_USER_IDS.ADAM_SILVER,
    status: "active",
  },
  {
    id: "participant-3",
    name: "Roger Goodell",
    email: "roger.goodell@example.com",
    role: "approver",
    permission: "review",
    userId: DEMO_USER_IDS.ROGER_GOODELL,
    status: "active",
  },
];

// ==================== DEAL STATUS HELPERS ====================
export const getDealStatusColor = (status: DemoDeal["status"]) => {
  switch (status) {
    case "Completed": return "bg-emerald-500/20 text-emerald-400";
    case "In Progress": return "bg-blue-500/20 text-blue-400";
    case "Pending": return "bg-amber-500/20 text-amber-400";
    case "Draft": return "bg-muted text-muted-foreground";
    default: return "bg-muted text-muted-foreground";
  }
};

// ==================== HELPERS ====================
export const getDemoDealById = (id: string): DemoDeal | undefined => {
  return demoDeals.find(deal => deal.id === id);
};

export const getDemoContractById = (id: string): DemoContract | undefined => {
  return demoContractsData.find(contract => contract.contractId === id);
};

// Re-export commonly used items from other demo files for convenience
export { DEMO_CONTRACT_IDS, DEMO_USER_IDS, demoUserDisplayInfo, demoProfilePictures } from "./demoContractData";
